import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
	Users, Briefcase, PauseCircle, CheckCircle, XCircle, Clock,
	DollarSign, TrendingUp, AlertTriangle, ArrowRight,
} from "lucide-react";
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
	Legend,
	PieChart,
	Pie,
	Cell,
} from "recharts";
import type { WorkforceKPIData } from "@/hooks/dashboard/useDashboardKPIs";
import { KPITooltip } from "./KPITooltip";

interface WorkforceKPISectionProps {
	data: WorkforceKPIData;
	tooltip?: string;
}

const STATUS_COLORS: Record<string, string> = {
	active: "#22c55e",
	on_hold: "#f59e0b",
	completed: "#3b82f6",
	terminated: "#ef4444",
	on_bench: "#8b5cf6",
};

const CONTRACT_COLORS: Record<string, string> = {
	freelance: "#8b5cf6",
	employee: "#3b82f6",
	contractor: "#f59e0b",
	unknown: "#94a3b8",
};

const BENCH_REASON_COLORS: Record<string, string> = {
	between_assignments: "#8b5cf6",
	awaiting_start: "#3b82f6",
	client_cancelled: "#ef4444",
	training: "#22c55e",
	project_ended: "#f59e0b",
	other: "#94a3b8",
	unknown: "#cbd5e1",
};

function formatCurrency(value: number): string {
	return new Intl.NumberFormat(undefined, {
		style: "currency",
		currency: "EUR",
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	}).format(value);
}

export function WorkforceKPISection({ data, tooltip }: WorkforceKPISectionProps) {
	const { t } = useTranslation();

	const hasFinancials = data.financials.dailyRevenue > 0 || data.financials.dailyBenchCost > 0;

	return (
		<div className="space-y-6">
			{/* Workforce Header */}
			<div className="flex items-center gap-2">
				<Users className="h-5 w-5 text-muted-foreground" />
				<h3 className="text-xl font-semibold">{t("dashboard.kpi.workforce.title", "Workforce Management")}</h3>
				{tooltip && <KPITooltip text={tooltip} />}
			</div>

			{/* KPI Cards Row */}
			<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
				<Card>
					<CardContent className="pt-4 pb-3 px-4">
						<div className="flex items-center gap-2 mb-1">
							<Briefcase className="h-4 w-4 text-blue-500" />
							<span className="text-xs text-muted-foreground">{t("dashboard.kpi.workforce.total", "Total")}</span>
						</div>
						<div className="text-2xl font-semibold">{data.totalAssignments}</div>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="pt-4 pb-3 px-4">
						<div className="flex items-center gap-2 mb-1">
							<CheckCircle className="h-4 w-4 text-green-500" />
							<span className="text-xs text-muted-foreground">{t("dashboard.kpi.workforce.active", "Active")}</span>
						</div>
						<div className="text-2xl font-semibold text-green-600">{data.activeAssignments}</div>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="pt-4 pb-3 px-4">
						<div className="flex items-center gap-2 mb-1">
							<Users className="h-4 w-4 text-purple-500" />
							<span className="text-xs text-muted-foreground">{t("dashboard.kpi.workforce.bench", "On Bench")}</span>
						</div>
						<div className="text-2xl font-semibold text-purple-600">{data.benchCount}</div>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="pt-4 pb-3 px-4">
						<div className="flex items-center gap-2 mb-1">
							<PauseCircle className="h-4 w-4 text-amber-500" />
							<span className="text-xs text-muted-foreground">{t("dashboard.kpi.workforce.onHold", "On Hold")}</span>
						</div>
						<div className="text-2xl font-semibold text-amber-600">{data.onHoldCount}</div>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="pt-4 pb-3 px-4">
						<div className="flex items-center gap-2 mb-1">
							<XCircle className="h-4 w-4 text-red-500" />
							<span className="text-xs text-muted-foreground">{t("dashboard.kpi.workforce.ended", "Ended")}</span>
						</div>
						<div className="text-2xl font-semibold text-red-600">{data.completedCount + data.terminatedCount}</div>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="pt-4 pb-3 px-4">
						<div className="flex items-center gap-2 mb-1">
							<Clock className="h-4 w-4 text-sky-500" />
							<span className="text-xs text-muted-foreground">{t("dashboard.kpi.workforce.avgDuration", "Avg Duration")}</span>
						</div>
						<div className="text-2xl font-semibold text-sky-600">
							{data.avgAssignmentDuration != null ? `${data.avgAssignmentDuration}d` : "—"}
						</div>
					</CardContent>
				</Card>
			</div>

			{/* Financial KPIs */}
			{hasFinancials && (
				<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
					<Card>
						<CardContent className="pt-4 pb-3 px-4">
							<div className="flex items-center gap-2 mb-1">
								<DollarSign className="h-4 w-4 text-green-500" />
								<span className="text-xs text-muted-foreground">{t("dashboard.kpi.workforce.dailyRevenue", "Daily Revenue")}</span>
							</div>
							<div className="text-xl font-semibold text-green-600">{formatCurrency(data.financials.dailyRevenue)}</div>
							<div className="text-xs text-muted-foreground mt-0.5">
								~{formatCurrency(data.financials.monthlyRevenue)}/mo
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-4 pb-3 px-4">
							<div className="flex items-center gap-2 mb-1">
								<DollarSign className="h-4 w-4 text-blue-500" />
								<span className="text-xs text-muted-foreground">{t("dashboard.kpi.workforce.dailyCost", "Daily Cost")}</span>
							</div>
							<div className="text-xl font-semibold text-blue-600">{formatCurrency(data.financials.dailyCost)}</div>
							<div className="text-xs text-muted-foreground mt-0.5">
								~{formatCurrency(data.financials.monthlyCost)}/mo
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-4 pb-3 px-4">
							<div className="flex items-center gap-2 mb-1">
								<TrendingUp className="h-4 w-4 text-emerald-500" />
								<span className="text-xs text-muted-foreground">{t("dashboard.kpi.workforce.dailyMargin", "Daily Margin")}</span>
							</div>
							<div className={`text-xl font-semibold ${data.financials.dailyMargin >= 0 ? "text-emerald-600" : "text-red-600"}`}>
								{formatCurrency(data.financials.dailyMargin)}
							</div>
							<div className="text-xs text-muted-foreground mt-0.5">
								{data.financials.marginPct}% · ~{formatCurrency(data.financials.monthlyMargin)}/mo
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-4 pb-3 px-4">
							<div className="flex items-center gap-2 mb-1">
								<AlertTriangle className="h-4 w-4 text-red-500" />
								<span className="text-xs text-muted-foreground">{t("dashboard.kpi.workforce.benchBurn", "Bench Burn")}</span>
							</div>
							<div className="text-xl font-semibold text-red-600">{formatCurrency(data.financials.dailyBenchCost)}</div>
							<div className="text-xs text-muted-foreground mt-0.5">
								~{formatCurrency(data.financials.monthlyBenchCost)}/mo
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-4 pb-3 px-4">
							<div className="flex items-center gap-2 mb-1">
								<DollarSign className="h-4 w-4 text-amber-500" />
								<span className="text-xs text-muted-foreground">{t("dashboard.kpi.workforce.netDaily", "Net Daily P&L")}</span>
							</div>
							<div className={`text-xl font-semibold ${(data.financials.dailyMargin - data.financials.dailyBenchCost) >= 0 ? "text-emerald-600" : "text-red-600"}`}>
								{formatCurrency(data.financials.dailyMargin - data.financials.dailyBenchCost)}
							</div>
							<div className="text-xs text-muted-foreground mt-0.5">
								~{formatCurrency((data.financials.monthlyMargin - data.financials.monthlyBenchCost))}/mo
							</div>
						</CardContent>
					</Card>
				</div>
			)}

			{/* Row 2: Utilization + Assignment Trend */}
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				{/* Utilization Rate */}
				<Card>
					<CardHeader>
						<div className="flex items-center gap-2">
							<CardTitle className="text-lg">{t("dashboard.kpi.workforce.utilization", "Utilization Rate")}</CardTitle>
							<KPITooltip text={t("dashboard.kpi.workforce.utilizationTooltip", "Percentage of active consultants assigned to a job (not on bench)")} />
						</div>
					</CardHeader>
					<CardContent>
						<div className="flex items-center justify-center">
							<div className="relative w-32 h-32">
								<svg className="w-full h-full" viewBox="0 0 36 36">
									<path
										d="M18 2.0845
											a 15.9155 15.9155 0 0 1 0 31.831
											a 15.9155 15.9155 0 0 1 0 -31.831"
										fill="none"
										stroke="#e5e7eb"
										strokeWidth="3"
									/>
									<path
										d="M18 2.0845
											a 15.9155 15.9155 0 0 1 0 31.831
											a 15.9155 15.9155 0 0 1 0 -31.831"
										fill="none"
										stroke="#22c55e"
										strokeWidth="3"
										strokeDasharray={`${data.utilizationRate}, 100`}
										strokeLinecap="round"
									/>
								</svg>
								<div className="absolute inset-0 flex items-center justify-center">
									<span className="text-2xl font-semibold">{data.utilizationRate}%</span>
								</div>
							</div>
						</div>
						<div className="mt-4 grid grid-cols-2 gap-4 text-sm text-center">
							<div>
								<div className="text-muted-foreground">{t("dashboard.kpi.workforce.assigned", "Assigned")}</div>
								<div className="text-lg font-medium text-green-600">{data.activeAssignments}</div>
							</div>
							<div>
								<div className="text-muted-foreground">{t("dashboard.kpi.workforce.bench", "On Bench")}</div>
								<div className="text-lg font-medium text-purple-600">{data.benchCount}</div>
							</div>
						</div>
					</CardContent>
				</Card>

				{/* Assignment Trend */}
				<Card>
					<CardHeader>
						<div className="flex items-center gap-2">
							<CardTitle className="text-lg">{t("dashboard.kpi.workforce.assignmentTrend", "Assignment Trend")}</CardTitle>
							<KPITooltip text={t("dashboard.kpi.workforce.assignmentTrendTooltip", "New assignments created vs assignments ended per month")} />
						</div>
					</CardHeader>
					<CardContent>
						{data.assignmentTrend.length === 0 ? (
							<div className="text-muted-foreground text-sm py-2">
								{t("dashboard.kpi.noData")}
							</div>
						) : (
							<div className="h-[250px]">
								<ResponsiveContainer width="100%" height="100%">
									<BarChart data={data.assignmentTrend} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
										<CartesianGrid strokeDasharray="3 3" opacity={0.1} />
										<XAxis dataKey="period" tick={{ fontSize: 11 }} />
										<YAxis />
										<Tooltip
											contentStyle={{
												backgroundColor: "rgba(255, 255, 255, 0.95)",
												borderRadius: "0.5rem",
												border: "none",
												boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
											}}
										/>
										<Legend />
										<Bar
											dataKey="created"
											name={t("dashboard.kpi.workforce.created", "Created")}
											fill="#22c55e"
											radius={[4, 4, 0, 0]}
										/>
										<Bar
											dataKey="ended"
											name={t("dashboard.kpi.workforce.ended", "Ended")}
											fill="#ef4444"
											radius={[4, 4, 0, 0]}
										/>
									</BarChart>
								</ResponsiveContainer>
							</div>
						)}
					</CardContent>
				</Card>
			</div>

			{/* Row 3: Contract Types + Bench Reasons */}
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				{/* Contract Type Distribution */}
				{data.contractTypes.length > 0 && (
					<Card>
						<CardHeader>
							<div className="flex items-center gap-2">
								<CardTitle className="text-lg">{t("dashboard.kpi.workforce.contractTypes", "Contract Types")}</CardTitle>
								<KPITooltip text={t("dashboard.kpi.workforce.contractTypesTooltip", "Distribution of assignments by contract type")} />
							</div>
						</CardHeader>
						<CardContent>
							<div className="flex items-center gap-6">
								<div className="h-[180px] w-[180px] shrink-0">
									<ResponsiveContainer width="100%" height="100%">
										<PieChart>
											<Pie
												data={data.contractTypes}
												dataKey="count"
												nameKey="type"
												cx="50%"
												cy="50%"
												innerRadius={45}
												outerRadius={75}
												paddingAngle={2}
											>
												{data.contractTypes.map((entry, i) => (
													<Cell key={i} fill={CONTRACT_COLORS[entry.type] || "#94a3b8"} />
												))}
											</Pie>
											<Tooltip />
										</PieChart>
									</ResponsiveContainer>
								</div>
								<div className="space-y-2 flex-1">
									{data.contractTypes.map(ct => (
										<div key={ct.type} className="flex items-center justify-between">
											<div className="flex items-center gap-2">
												<div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: CONTRACT_COLORS[ct.type] || "#94a3b8" }} />
												<span className="text-sm capitalize">
													{t(`dashboard.kpi.workforce.contractType.${ct.type}`, ct.type)}
												</span>
											</div>
											<span className="text-sm font-medium">{ct.count}</span>
										</div>
									))}
								</div>
							</div>
						</CardContent>
					</Card>
				)}

				{/* Bench Reasons */}
				{data.benchReasons.length > 0 && (
					<Card>
						<CardHeader>
							<div className="flex items-center gap-2">
								<CardTitle className="text-lg">{t("dashboard.kpi.workforce.benchReasons", "Bench Reasons")}</CardTitle>
								<KPITooltip text={t("dashboard.kpi.workforce.benchReasonsTooltip", "Why consultants are on the bench and associated daily cost")} />
							</div>
						</CardHeader>
						<CardContent>
							<div className="space-y-3">
								{data.benchReasons.map(br => {
									const color = BENCH_REASON_COLORS[br.reason] || "#94a3b8";
									return (
										<div key={br.reason} className="flex items-center gap-3">
											<div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: color }} />
											<span className="text-sm flex-1 capitalize">
												{t(`dashboard.kpi.workforce.benchReason.${br.reason}`, br.reason.replace(/_/g, " "))}
											</span>
											<Badge variant="secondary" className="text-xs">{br.count}</Badge>
											{br.dailyCost > 0 && (
												<span className="text-xs text-red-500 font-medium w-20 text-right">
													{formatCurrency(br.dailyCost)}/d
												</span>
											)}
										</div>
									);
								})}
							</div>
						</CardContent>
					</Card>
				)}
			</div>

			{/* Row 4: Expiring Soon */}
			{data.expiringSoon.length > 0 && (
				<Card>
					<CardHeader>
						<div className="flex items-center gap-2">
							<AlertTriangle className="h-5 w-5 text-amber-500" />
							<CardTitle className="text-lg">{t("dashboard.kpi.workforce.expiringSoon", "Assignments Ending Soon")}</CardTitle>
							<KPITooltip text={t("dashboard.kpi.workforce.expiringSoonTooltip", "Active assignments with expected end date within the next 30 days")} />
						</div>
					</CardHeader>
					<CardContent>
						<div className="space-y-2">
							{data.expiringSoon.map(a => (
								<div key={a.id} className="flex items-center gap-3 px-3 py-2 rounded-lg border hover:bg-muted/50 transition-colors">
									<div className={`shrink-0 text-sm font-semibold w-8 text-center ${
										a.daysRemaining <= 7 ? "text-red-600" : a.daysRemaining <= 14 ? "text-amber-600" : "text-blue-600"
									}`}>
										{a.daysRemaining}d
									</div>
									<div className="flex-1 min-w-0">
										<div className="text-sm font-medium truncate">{a.candidateName}</div>
										<div className="text-xs text-muted-foreground truncate">{a.jobTitle}</div>
									</div>
									<ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
									<span className="text-xs text-muted-foreground shrink-0">
										{new Date(a.expectedEndDate).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
									</span>
									{a.dailyRevenue != null && (
										<span className="text-xs font-medium text-green-600 shrink-0 w-16 text-right">
											{formatCurrency(a.dailyRevenue)}/d
										</span>
									)}
								</div>
							))}
						</div>
					</CardContent>
				</Card>
			)}
		</div>
	);
}
